import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

interface ServiceItem {
  title: string;
  description: string;
  details: string[];
  image: string;
}

export default function TagliandiPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'Tagliando Auto e Moto a Brescia e Provincia - Cambio Olio Brescia | Metamorfosi Preparazioni',
    description: 'Tagliando auto e moto a domicilio a Brescia e provincia. Cambio olio motore, filtro olio, filtro aria e filtro abitacolo. Manutenzione programmata secondo le specifiche del costruttore. Servizio professionale a casa tua a Brescia.',
    keywords: 'tagliando Brescia, tagliando auto Brescia, tagliando moto Brescia, cambio olio Brescia, filtro olio Brescia, filtro aria Brescia, manutenzione programmata Brescia, tagliando domicilio Brescia, meccanico Brescia provincia',
    canonical: '/tagliandi',
    ogTitle: 'Tagliando Auto e Moto a Brescia - Cambio Olio a Domicilio',
    ogDescription: 'Tagliandi completi per auto e moto a Brescia e provincia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Tagliandi Auto e Moto a Brescia',
      provider: {
        '@type': 'AutoRepair',
        name: 'Metamorfosi Preparazioni',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
        telephone: '+39-351-831-2394',
        email: 'metamorfosi.preparazioni@gmail.com'
      },
      areaServed: {
        '@type': 'City',
        name: 'Brescia',
        containedIn: {
          '@type': 'AdministrativeArea',
          name: 'Provincia di Brescia'
        }
      },
      description: 'Tagliandi base e completi per auto e moto a domicilio a Brescia e provincia. Include cambio olio motore, sostituzione filtro olio, filtro aria e filtro abitacolo secondo le specifiche del costruttore.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '$$'
      }
    }
  });

  const serviceItems: ServiceItem[] = [
    {
      title: 'Olio Motore',
      description: 'Il cuore della lubrificazione del tuo motore. L\'olio motore riduce l\'attrito tra le parti meccaniche, previene l\'usura e mantiene il motore pulito.',
      details: [
        'Riduce l\'attrito e l\'usura delle parti mobili',
        'Raffredda i componenti interni del motore',
        'Protegge dalla corrosione e dai depositi',
        'Intervallo consigliato: ogni 15.000-30.000 km'
      ],
      image: 'https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/b45247a02ea79e343c4e0248ff22cf0f.webp'
    },
    {
      title: 'Filtro Olio',
      description: 'Il guardiano della purezza dell\'olio. Cattura le impurità e i residui metallici, garantendo che solo olio pulito circoli nel motore.',
      details: [
        'Filtra particelle metalliche e impurità',
        'Prolunga la vita dell\'olio motore',
        'Protegge i componenti interni del motore',
        'Da sostituire ad ogni cambio olio'
      ],
      image: 'https://public.readdy.ai/ai/img_res/5e319c6b4cf73f3f9f626e4ba39f7544.jpg'
    },
    {
      title: 'Filtro Aria',
      description: 'I polmoni del tuo motore. Garantisce che solo aria pulita entri nella camera di combustione, ottimizzando prestazioni e consumi.',
      details: [
        'Blocca polvere, polline e detriti',
        'Migliora l\'efficienza del carburante',
        'Aumenta le prestazioni del motore',
        'Intervallo consigliato: ogni 20.000-40.000 km'
      ],
      image: 'https://readdy.ai/api/search-image?query=New%20car%20air%20filter%20rectangular%20pleated%20paper%20filter%20element%20automotive%20spare%20part%20clean%20white%20background%20professional%20product%20photography%20high%20quality%20detail%20engine%20maintenance%20component&width=600&height=400&seq=airfilter001&orientation=landscape'
    },
    {
      title: 'Filtro Abitacolo',
      description: 'La protezione per te e i tuoi passeggeri. Purifica l\'aria che entra nell\'abitacolo, filtrando pollini, polveri sottili e cattivi odori.',
      details: [
        'Filtra pollini e allergeni',
        'Blocca polveri sottili PM2.5 e PM10',
        'Elimina cattivi odori dall\'esterno',
        'Intervallo consigliato: ogni 15.000-20.000 km'
      ],
      image: 'https://readdy.ai/api/search-image?query=New%20cabin%20air%20filter%20for%20car%20white%20pleated%20filter%20media%20rectangular%20shape%20automotive%20HVAC%20component%20clean%20product%20photography%20white%20background%20high%20detail%20interior%20air%20quality%20filter&width=600&height=400&seq=cabinfilter001&orientation=landscape'
    }
  ];

  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('contatti');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 sm:h-12 md:h-14 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center text-white hover:text-[#E31E24] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-semibold text-sm">Torna alla Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="inline-block mb-4">
            <span className="inline-block px-6 py-2 border-2 border-[#E31E24] text-[#E31E24] bg-white font-bold text-xs tracking-widest rounded-full uppercase">
              Servizio Completo
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6">
            Tagliandi Base e Completi
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Manutenzione programmata secondo le specifiche del costruttore. Scopri tutti i componenti che controlliamo e sostituiamo per mantenere il tuo veicolo in perfette condizioni.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="space-y-16 md:space-y-24">
            {serviceItems.map((item, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 lg:gap-16`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <div className="w-full h-64 sm:h-80 md:h-96">
                      <img 
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-[#E31E24] flex items-center justify-center mr-4">
                      <i className="ri-checkbox-circle-line text-white text-2xl"></i>
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black">
                      {item.title}
                    </h2>
                  </div>
                  <p className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed">
                    {item.description}
                  </p>
                  <ul className="space-y-3">
                    {item.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start">
                        <div className="w-6 h-6 rounded-full bg-[#E31E24]/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-[#E31E24] text-sm"></i>
                        </div>
                        <span className="text-gray-700 text-sm sm:text-base">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Prenota il Tuo Tagliando
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto">
            Contattaci per un preventivo gratuito. Il nostro servizio a domicilio ti permette di risparmiare tempo e denaro.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={scrollToContact}
              className="inline-flex items-center bg-[#E31E24] text-white font-bold text-sm px-8 py-4 rounded-full hover:bg-[#c41a1f] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span className="mr-3">RICHIEDI PREVENTIVO</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <a 
              href="https://wa.me/393518312394"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-black font-bold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-[#25D366] text-xl mr-3"></i>
              <span>WHATSAPP</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E31E24] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col items-center space-y-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center">
              <i className="ri-mail-line text-white text-lg mr-2"></i>
              <a href="mailto:metamorfosi.preparazioni@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer text-xs sm:text-sm break-all">
                metamorfosi.preparazioni@gmail.com
              </a>
            </div>
            <p className="text-white/80 text-xs sm:text-sm text-center">
              © 2025 Metamorfosi Preparazioni. Tutti i diritti riservati.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-white text-xs sm:text-sm transition-colors duration-300 cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
